import mapboxgl from 'mapbox-gl';
import moment from 'moment-timezone';

// ── Timezone-boundary overlay for the shared Track map ─────────────────────────
// Draws world timezone boundaries (Natural Earth 10m dataset, simplified to
// ~270KB) as dashed lines + two-line labels giving the zone's UTC offset AND
// its CURRENT local time ("UTC−5" / "11:42 AM"). The dataset lives in
// /public/data/timezones.geojson so it never enters the JS bundle — it's fetched
// once, on the first enable, and cached at module level for the session.
//
// Ownership: TrackMapLayout (the owner of the single persistent Mapbox instance)
// re-adds these layers on every `style.load`, so the overlay survives theme
// changes triggered from EITHER the list or the detail page without those pages
// knowing it exists.
//
// Ported from wal-frontend-v2 src/app/Radar/radar.timezone.ts. Two deliberate
// differences: the asset URL uses CRA's process.env.PUBLIC_URL (this app is
// Create React App; `import.meta.env` does not exist here), and the beforeId
// candidates use THIS app's route layer ids.

export const TIMEZONE_SOURCE_ID = 'timezone-boundaries';
const TIMEZONE_LINE_LAYER_ID = 'timezone-lines';
export const TIMEZONE_LABEL_LAYER_ID = 'timezone-labels';
// Separate source holding one anchor feature per zone, so the labels can render
// with collision disabled without printing a zone's clock once per polygon.
const TIMEZONE_LABEL_SOURCE_ID = 'timezone-label-anchors';

// The overlay is NOT persisted — like the map theme, it resets to
// its default (off) on every page load. The user re-enables it per session.

// Session-level cache of the boundary data. Kept as the promise (not the result)
// so concurrent callers share one in-flight fetch; reset on failure so a later
// toggle can retry after a network blip.
let timezoneDataPromise: Promise<GeoJSON.FeatureCollection> | null = null;

const loadTimezoneData = (): Promise<GeoJSON.FeatureCollection> => {
    if (!timezoneDataPromise) {
        timezoneDataPromise = fetch(`${process.env.PUBLIC_URL}/data/timezones.geojson`).then((res) => {
            if (!res.ok) {
                throw new Error(`Failed to load timezone boundaries (${res.status})`);
            }

            return res.json();
        });
        timezoneDataPromise.catch(() => {
            timezoneDataPromise = null;
        });
    }

    return timezoneDataPromise;
};

// Per-theme overlay colors. The muted gray that reads as "reference line" on the
// Light theme disappears on Satellite imagery and fights the terrain tints on
// Outdoors, so each base style gets its own line/label/halo triple. Resolved from
// the live style at layer-add time — the layout re-adds these layers on every
// `style.load`, so a theme switch repaints them without any extra plumbing.
type TimezoneTheme = { line: string; text: string; halo: string };

const TIMEZONE_THEME_COLORS: Record<'light' | 'streets' | 'satellite' | 'outdoors', TimezoneTheme> = {
    // Pale basemap: mid-gray stays legible without competing with routes.
    light: { line: '#667085', text: '#475467', halo: '#ffffff' },
    // Busier basemap with its own gray roads — go a step darker to separate.
    streets: { line: '#475467', text: '#344054', halo: '#ffffff' },
    // Dark imagery: gray vanishes. White lines + a dark halo on the labels.
    satellite: { line: '#ffffff', text: '#ffffff', halo: '#101828' },
    // Green/tan terrain tints: cool slate reads as an overlay, not a contour.
    outdoors: { line: '#3538CD', text: '#2D31A6', halo: '#ffffff' },
};

// Mapbox exposes the loaded style's URL on `sprite`/`glyphs`-adjacent metadata
// inconsistently across versions, so match on the style name Mapbox reports.
// Order matters: 'Satellite Streets' contains 'streets', so test satellite first.
const resolveTimezoneTheme = (map: mapboxgl.Map): TimezoneTheme => {
    let name = '';

    try {
        name = (map.getStyle()?.name ?? '').toLowerCase();
    } catch {
        // getStyle() throws if the style is mid-swap; fall through to the default.
    }

    if (name.includes('satellite')) return TIMEZONE_THEME_COLORS.satellite;
    if (name.includes('outdoors')) return TIMEZONE_THEME_COLORS.outdoors;
    if (name.includes('streets')) return TIMEZONE_THEME_COLORS.streets;

    return TIMEZONE_THEME_COLORS.light;
};

// Each boundary feature carries a numeric UTC offset in hours (`zone`, e.g. -5,
// 5.5, 12.75) plus a pre-rendered `label` ("UTC−5").
//
// The dataset's `zone` is a fixed STANDARD-time offset with no DST awareness, so
// shifting "now" by it directly would show the wrong hour for every region that
// observes DST — including the four North American zones our ops team actually
// dispatches into (in summer, the UTC−5 band's civil time is 12:42 PM, not the
// 11:42 AM a raw offset shift produces). So for the offsets that cover
// US/Canada/Mexico we resolve the clock through a representative IANA zone and
// let moment-timezone apply the DST rules; the rest of the world keeps the raw
// offset shift, which is correct for the majority of those bands and never worse
// than the boundary data itself.
//
// The offset shown next to the clock is likewise re-derived from the resolved
// zone (see zoneOffsetLabel) so the two lines can never disagree — a label
// reading "UTC−5 / 12:42 PM" would be self-contradictory.
const NORTH_AMERICAN_ZONE_BY_OFFSET: Record<string, string> = {
    '-10': 'Pacific/Honolulu', // Hawaii — no DST
    '-9': 'America/Anchorage', // Alaska
    '-8': 'America/Los_Angeles', // Pacific
    '-7': 'America/Denver', // Mountain
    '-6': 'America/Chicago', // Central
    '-5': 'America/New_York', // Eastern
    '-4': 'America/Halifax', // Atlantic
    '-3.5': 'America/St_Johns', // Newfoundland
};

const resolveIanaZone = (offsetHours: number): string | undefined => {
    const name = NORTH_AMERICAN_ZONE_BY_OFFSET[String(offsetHours)];

    // moment.tz.zone() guards against a tz-data build that omits the name.
    return name && moment.tz.zone(name) ? name : undefined;
};

// "UTC−5" / "UTC+5:30" for the offset the clock is actually computed in. Uses the
// same U+2212 minus sign as the dataset's own labels so the overlay stays visually
// consistent whichever branch produced the text.
const zoneOffsetLabel = (offsetHours: number): string => {
    const sign = offsetHours < 0 ? '−' : '+';
    const abs = Math.abs(offsetHours);
    const wholeHours = Math.floor(abs);
    const minutes = Math.round((abs - wholeHours) * 60);

    return `UTC${sign}${wholeHours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

// The offset + local time for one feature at `now`. DST-observing North American
// bands go through moment-timezone; everywhere else shifts UTC by the fixed offset
// and reads the result back in UTC.
const zoneClock = (offsetHours: number, now: number): { offsetLabel: string; clock: string } => {
    const ianaZone = resolveIanaZone(offsetHours);

    if (ianaZone) {
        const local = moment(now).tz(ianaZone);

        return { offsetLabel: zoneOffsetLabel(local.utcOffset() / 60), clock: local.format('h:mm A') };
    }

    const shifted = new Date(now + offsetHours * 3600000);

    let hours = shifted.getUTCHours();
    const minutes = shifted.getUTCMinutes();
    const meridiem = hours < 12 ? 'AM' : 'PM';

    hours = hours % 12 || 12;

    return { offsetLabel: zoneOffsetLabel(offsetHours), clock: `${hours}:${String(minutes).padStart(2, '0')} ${meridiem}` };
};

// One label per zone, not per polygon.
//
// The dataset splits several zones across many polygons (zone +5 has 8), and the
// label layer would otherwise print the same clock once per polygon. That was
// tolerable while collision detection culled the duplicates — but collision is
// exactly what has to be switched OFF to stop the labels flickering on every
// update (Mapbox re-runs placement on any text change, and the fade in/out of
// re-placed labels is the flicker). So the duplicates are removed from the data
// instead: each zone keeps only its largest polygon as the label anchor.
//
// Area is approximated by bounding box — good enough to pick "the big one" (the
// mainland band rather than an offshore sliver) and far cheaper than a real
// polygon area over 119 features.
const boundingBoxArea = (geometry: GeoJSON.Geometry): number => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const walk = (coords: any): void => {
        if (typeof coords?.[0] === 'number') {
            minX = Math.min(minX, coords[0]);
            maxX = Math.max(maxX, coords[0]);
            minY = Math.min(minY, coords[1]);
            maxY = Math.max(maxY, coords[1]);

            return;
        }

        if (Array.isArray(coords)) coords.forEach(walk);
    };

    walk((geometry as { coordinates?: unknown })?.coordinates);

    if (minX === Infinity) return 0;

    return (maxX - minX) * (maxY - minY);
};

// The label-layer source: the same boundaries, reduced to one feature per zone.
// Kept separate from the line layer's source so the dashed boundaries still draw
// every polygon — only the labels are de-duplicated.
const buildLabelAnchors = (data: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection => {
    const largestByZone = new Map<string, { area: number; feature: GeoJSON.Feature }>();

    data.features.forEach((feature) => {
        const zone = Number(feature.properties?.zone);

        if (!Number.isFinite(zone)) return;

        const key = String(zone);
        const area = boundingBoxArea(feature.geometry);
        const current = largestByZone.get(key);

        if (!current || area > current.area) {
            largestByZone.set(key, { area, feature });
        }
    });

    return { type: 'FeatureCollection', features: Array.from(largestByZone.values()).map((entry) => entry.feature) };
};

// The label text is driven by a LAYOUT EXPRESSION, not by per-feature properties.
//
// Why: updating the clock used to mean source.setData() with the `clock` property
// rewritten on all 119 features. Mapbox treats that as brand-new data — it drops
// every symbol, re-runs collision detection and re-places the labels — so the whole
// overlay visibly flickered once a minute (labels near a boundary would vanish and
// pop back). Writing a `match` expression on `text-field` instead leaves the source
// and its geometry completely untouched: Mapbox only re-evaluates the text, so the
// symbols keep their existing placement and nothing jumps.
//
// The expression maps the feature's `zone` to that zone's current two-line label.
// 40 distinct zones → 40 branches, ~1KB of JSON.
//
// IMPORTANT — the match labels MUST be strings, not raw numbers. Mapbox's `match`
// only accepts integers or strings as case labels, and 13 of the zones are
// fractional (-9.5, 5.75, 12.75, …). Passing those as numbers makes the WHOLE
// expression invalid, which Mapbox rejects silently — the result is no labels at
// all, not just the fractional ones. So both sides are stringified: ['to-string',
// ['get','zone']] on the input, String(zone) on each case.
const buildTimezoneLabelExpression = (data: GeoJSON.FeatureCollection, now: number): mapboxgl.ExpressionSpecification => {
    const zones = Array.from(new Set(data.features.map((feature) => Number(feature.properties?.zone)).filter((zone): zone is number => Number.isFinite(zone))));

    const branches = zones.flatMap((zone) => {
        const { offsetLabel, clock } = zoneClock(zone, now);

        return [String(zone), `${offsetLabel}\n${clock}`];
    });

    // `match` requires at least one branch; with none (an empty/failed dataset) fall
    // back to the feature's own pre-rendered offset label so the lines stay annotated.
    if (!branches.length) {
        return ['coalesce', ['get', 'label'], ''] as unknown as mapboxgl.ExpressionSpecification;
    }

    // Final '' is the required default for an unmatched zone — renders no label
    // rather than a wrong time.
    return ['match', ['to-string', ['get', 'zone']], ...branches, ''] as unknown as mapboxgl.ExpressionSpecification;
};

// Refreshes the overlay's clock labels to `now`. No-op when the overlay was never
// added (layer absent) or the map is gone — the caller ticks on a timer and must not
// care about either case.
//
// Unlike the old setData path this touches ONLY the label layer's text-field, so the
// line layer, the source and every symbol's placement are left exactly as they are.
export const refreshTimezoneClockLabels = (map: mapboxgl.Map, now: number = Date.now()): void => {
    if ((map as { _removed?: boolean })._removed) return;

    if (!map.getLayer(TIMEZONE_LABEL_LAYER_ID) || !timezoneDataPromise) return;

    void timezoneDataPromise.then((data) => {
        if ((map as { _removed?: boolean })._removed || !map.getLayer(TIMEZONE_LABEL_LAYER_ID)) return;

        map.setLayoutProperty(TIMEZONE_LABEL_LAYER_ID, 'text-field', buildTimezoneLabelExpression(data, now));
    });
};

// Adds the timezone GeoJSON source + line/label layers. Idempotent (bails if the
// source already exists) and safe against the three races the async fetch opens
// up: a theme change wiping the style mid-fetch, the map being removed entirely,
// and the user toggling the overlay off (or off-then-on) before the fetch lands.
//
// `isStillWanted` is read AFTER the await, right before the layers are added, so
// the layers' initial `visibility` reflects the CURRENT toggle state — not the
// state at call time. Pass a getter that reads the live ref (not a captured
// boolean): if the user turned the overlay off during the ~271KB fetch, the
// layers are added hidden instead of flashing on. Defaults to visible for callers
// that always add-then-show (the layout's style.load re-init handler).
export const addTimezoneSourceAndLayers = async (map: mapboxgl.Map, isStillWanted: () => boolean = () => true): Promise<void> => {
    if (map.getSource(TIMEZONE_SOURCE_ID)) return;

    let data: GeoJSON.FeatureCollection;

    try {
        data = await loadTimezoneData();
    } catch (err) {
        console.error('Track timezone overlay: could not load boundary data', err);

        return;
    }

    // Re-check after the await: `_removed` guards against the layout unmounting
    // (map.remove()) while the fetch was in flight — mapbox throws on any style
    // access after removal; the getSource re-check guards a concurrent add.
    if ((map as { _removed?: boolean })._removed || map.getSource(TIMEZONE_SOURCE_ID)) return;

    // Snapshot the live toggle state now (post-await) and bake it into the
    // layers' initial visibility, closing the rapid-toggle race.
    const initialVisibility = isStillWanted() ? 'visible' : 'none';

    // Raw data: the clock text lives in the label layer's expression, not in the
    // features, so nothing here has to be rewritten when the time changes.
    // Each addSource is guarded independently: the two sources are added together, but
    // a style swap mid-fetch could in principle leave one behind, and addSource throws
    // on a duplicate id — which would abort before the layers were added and leave the
    // overlay permanently blank for the session.
    if (!map.getSource(TIMEZONE_SOURCE_ID)) {
        map.addSource(TIMEZONE_SOURCE_ID, { type: 'geojson', data });
    }

    if (!map.getSource(TIMEZONE_LABEL_SOURCE_ID)) {
        map.addSource(TIMEZONE_LABEL_SOURCE_ID, { type: 'geojson', data: buildLabelAnchors(data) });
    }

    // Insert beneath the data layers when they already exist — the list page's
    // cluster circles or the detail page's route lines — so order data always
    // draws above the boundary lines. When the overlay is added first (layout's
    // style.load handler runs before the children's), the children's layers get
    // appended on top anyway. HTML markers (the arrow-in-dot order pins) are
    // DOM elements above the canvas regardless. NOTE the ids differ from WAL's:
    // this app names its route LAYERS 'route-traveled-line' / 'route-remaining-line'
    // (the sources are 'route-traveled' / 'route-remaining'), and getLayer()
    // needs the layer id, not the source id.
    const beforeId = ['clusters', 'route-traveled-line', 'route-remaining-line'].find((layerId) => map.getLayer(layerId));

    const theme = resolveTimezoneTheme(map);

    map.addLayer(
        {
            id: TIMEZONE_LINE_LAYER_ID,
            type: 'line',
            source: TIMEZONE_SOURCE_ID,
            layout: {
                visibility: initialVisibility,
            },
            paint: {
                // Dashes so the overlay reads as reference lines, not data; the
                // color is per-theme (see TIMEZONE_THEME_COLORS) so it stays
                // visible on all four base styles without competing with routes
                // or cluster pins.
                'line-color': theme.line,
                'line-opacity': 0.75,
                'line-width': 1.75,
                'line-dasharray': [2, 2],
            },
        },
        beforeId
    );

    map.addLayer(
        {
            id: TIMEZONE_LABEL_LAYER_ID,
            type: 'symbol',
            source: TIMEZONE_LABEL_SOURCE_ID,
            layout: {
                visibility: initialVisibility,
                // Two lines: the UTC offset above its current local time. `concat`
                // with a literal newline rather than 'text-field': [...] array form
                // so Mapbox treats it as one formatted string per feature.
                // Seeded for the current minute; refreshTimezoneClockLabels swaps this
                // same property on each minute tick.
                'text-field': buildTimezoneLabelExpression(data, Date.now()),
                'text-line-height': 1.3,
                'text-justify': 'center',
                // Same font stack family the cluster-count layer relies on — present
                // in all four selectable Mapbox base styles.
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
                'text-size': 11,
                // The label is now two lines instead of one, so it needs more room
                // before Mapbox's collision detection starts dropping labels — at
                // the default padding, adjacent narrow bands (and the order pins)
                // knocked out whole zones' clocks.
                // Collision detection OFF. This is what stops the flicker: Mapbox re-runs
                // symbol placement on ANY text change, and labels that get re-placed fade
                // out and back in — which is precisely the jerk seen once a minute. With
                // one anchor per zone (buildLabelAnchors) there are no duplicate clocks to
                // cull, so nothing is lost by disabling it.
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                // The boundary features are polygons; 'point' places one label at
                // each polygon's centroid. Allowing overlap is deliberately NOT set —
                // dropping a colliding clock is better than stacking two unreadable
                // ones on top of each other.
                'symbol-placement': 'point',
            },
            paint: {
                'text-color': theme.text,
                'text-halo-color': theme.halo,
                'text-halo-width': 1,
            },
        },
        beforeId
    );
};

// Show/hide the overlay without dropping the source — toggling back on is then
// instant (no re-fetch, no re-add).
export const setTimezoneLayersVisibility = (map: mapboxgl.Map, visible: boolean): void => {
    const visibility = visible ? 'visible' : 'none';

    [TIMEZONE_LINE_LAYER_ID, TIMEZONE_LABEL_LAYER_ID].forEach((layerId) => {
        if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', visibility);
        }
    });
};
